import {
  HTTP_INTERCEPTORS,
  provideHttpClient,
  withInterceptorsFromDi,
  withJsonpSupport,
} from '@angular/common/http';
import {
  ErrorHandler,
  LOCALE_ID,
  NgModule,
  Optional,
  SkipSelf,
} from '@angular/core';
import { MatDateFnsModule } from '@angular/material-date-fns-adapter';
import {
  ENVIRONMENT,
  ERROR_MESSAGES,
  NOTIFICATION_PROVIDER,
  NotificationService,
  ErrorMessageService,
} from 'wflow-cdk';
import { environment } from 'wflow-main-app/src/environments';
import { DialogConfirmModule, IconsRegistry } from 'wflow-ui';
import { WUI_GOOGLE_MAPS_API_KEY } from 'wflow-ui/map';
import { EmptyComponent } from './components/empty.component';
import { throwIfAlreadyLoaded } from './guards/module-import.guard';
import { TrialPeriodExpiredGuard } from './guards/trial-period-expired.guard';
import { ApiErrorsInterceptor } from './interceptors/api-errors.interceptor';
import { MaintenanceInterceptor } from './interceptors/maintenance.interceptor';
import { MockApiInterceptor } from './interceptors/mock-api.interceptor';
import { NetworkRetryInterceptor } from './interceptors/network-retry.interceptor';
import { OrgAuthGatewayInterceptor } from './interceptors/org-auth-gateway.interceptor';
import { UserSessionIdInterceptorService } from './interceptors/user-session-id.interceptor';
import { notificationResolver } from './resolvers/notification.resolver';
import { SmartlookEventService } from './services/analytics/analytic-event.service';
import { BasicErrorHandlerService } from './services/error-handler/basic-error-handler.service';
import { SessionService } from './services/session.service';
import { ERROR_MESSAGES_LABELS } from '../shared/__const/error-messages.conts';

@NgModule({
  declarations: [EmptyComponent],
  exports: [],
  imports: [
    DialogConfirmModule,
    // Global date format
    MatDateFnsModule,
  ],
  providers: [
    TrialPeriodExpiredGuard,
    SessionService,
    SmartlookEventService,
    ...(environment?.enableMocking
      ? [
          {
            provide: HTTP_INTERCEPTORS,
            useClass: MockApiInterceptor,
            multi: true,
          },
        ]
      : []),
    {
      provide: HTTP_INTERCEPTORS,
      useClass: NetworkRetryInterceptor,
      multi: true,
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: OrgAuthGatewayInterceptor,
      multi: true,
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: ApiErrorsInterceptor,
      multi: true,
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: UserSessionIdInterceptorService,
      multi: true,
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: MaintenanceInterceptor,
      multi: true,
    },
    {
      provide: ErrorHandler,
      useClass: BasicErrorHandlerService,
    },
    {
      provide: ENVIRONMENT,
      useValue: environment,
    },
    {
      // wflow-ui renders the map; the key is the application's to supply. Root-provided, so the
      // token's dev-only fallback never applies inside the app itself.
      provide: WUI_GOOGLE_MAPS_API_KEY,
      useValue: environment.googleApiKey,
    },
    {
      provide: LOCALE_ID,
      deps: [SessionService],
      useFactory: (sessionService: SessionService) =>
        sessionService.getLocale(),
    },
    IconsRegistry,
    provideHttpClient(withInterceptorsFromDi(), withJsonpSupport()),
    NotificationService,
    {
      provide: NOTIFICATION_PROVIDER,
      useFactory: notificationResolver,
    },
    ErrorMessageService,
    {
      provide: ERROR_MESSAGES,
      useValue: ERROR_MESSAGES_LABELS,
    },
  ],
})
export class CoreModule {
  constructor(@Optional() @SkipSelf() parentModule: CoreModule) {
    throwIfAlreadyLoaded(parentModule, 'CoreModule');
  }
}
